import { memo, useCallback, useMemo, useState } from 'react';

import { useTrackRestoreAsset } from '@air/analytics';
import { ConfirmationModal } from '@air/component-confirmation-modal';
import { useAirModal } from '@air/provider-modal';
import { useToasts } from '@air/provider-toast';

import { type NewParentLocation, useAssetRestorationTask } from '../../hooks/useAssetRestorationTask';
import { type RecentlyDeletedItemAsset } from '../../types';
import { RecentlyDeletedNewParentSelectionModal } from './RecentlyDeletedNewParentSelectionModal';

export type RecentlyDeletedRecoverAssetModalParams = {
  id: string;
  title: string;
  parentBoards: RecentlyDeletedItemAsset['asset']['parentBoards'];
};

export const RecentlyDeletedRecoverAssetModal = memo(
  ({ id, title, onClose, parentBoards }: AirModalProps<RecentlyDeletedRecoverAssetModalParams>) => {
    const { createAssetRestorationTask } = useAssetRestorationTask();
    const [showRecentlyDeletedNewParentSelectionModal] = useAirModal(RecentlyDeletedNewParentSelectionModal);
    const hasNoActiveParent = useMemo(() => parentBoards.every((parent) => parent?.inactive), [parentBoards]);
    const [loading, setLoading] = useState(false);
    const { trackRestoreAsset } = useTrackRestoreAsset();
    const { showToast } = useToasts();

    const handleRecover = useCallback(
      // Parameter parent if `undefined` then it will be failed if there is no active parents.
      // If `root` then will restore as an unused asset.
      // If `board.id` then will add the asset to the selected parent after restoring.
      // The asset will always be restored to all original active parents.
      async (parent?: NewParentLocation) => {
        setLoading(true);
        const result = await createAssetRestorationTask({
          assets: [{ id, title }],
          parent,
        });
        if (result.data) {
          trackRestoreAsset({
            asset_id: id,
            asset_name: title,
          });
          onClose();
        } else if (result.error) {
          showToast(result.error.message || 'There was an error restoring the asset.');
        }
        setLoading(false);
      },
      [createAssetRestorationTask, id, onClose, showToast, title, trackRestoreAsset],
    );

    const handleConfirm = useCallback(async () => {
      if (hasNoActiveParent) {
        showRecentlyDeletedNewParentSelectionModal({
          onConfirm: handleRecover,
          canSelectLibrary: false,
          description: (
            <>
              <p>The original location of this asset has been modified or no longer exists.</p>
              <p className="mt-4">Select a new location to recover this asset to</p>
            </>
          ),
          rootOptionLabel: 'Restore asset as unused',
        });
        onClose();
      } else {
        // Try to restore the asset to it's original parents.
        await handleRecover();
      }
    }, [handleRecover, hasNoActiveParent, onClose, showRecentlyDeletedNewParentSelectionModal]);

    return (
      <ConfirmationModal
        id="recently-deleted-recover-assets"
        ctaPrimaryText="Recover"
        description="Recovering this asset will also recover all of its versions to all of its original locations within the workspace."
        isOpen
        onDismiss={onClose}
        onConfirm={handleConfirm}
        isDisabled={loading}
        isLoading={loading}
        title="Recover asset?"
      />
    );
  },
);

RecentlyDeletedRecoverAssetModal.displayName = 'RecentlyDeletedRecoverAssetModal';
